// src/pages/Billing.js
// Minimal "Subscription & Billing" page for the Stripe Billing phase.
//   - Not subscribed: shows the Starter plan + "Subscribe" (redirects to
//     Stripe Checkout via the server — the server picks the price).
//   - Subscribed: shows status, cancel (at period end) / reactivate, and
//     recent billing periods (paid amount + granted credit).
import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowBackIcon, CloseIcon } from '@chakra-ui/icons';
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  Divider,
  Flex,
  Heading,
  HStack,
  IconButton,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react';
import { Sparkle, Calendar, Rocket, TrendingUp } from 'lucide-react';
import Navbar from '../components/Navbar';
import Sidebar from '../components/Sidebar';
import { apiCall } from '../utils/apiCall';
import { notify } from '../context/NotificationContext';
import {
  formatDate,
  currencyLabel,
  displayPeriodRange,
  EARLY_ACCESS_OFFER,
  useBillingOutcomeNotification,
} from '../utils/billingHelpers';

const API =
  process.env.REACT_APP_WEB_SERVER_URL || 'http://localhost:8080';

function formatMoney(value) {
  if (value === null || value === undefined) return '—';
  const n = Number(value);
  if (Number.isNaN(n)) return value;
  return `$${n.toFixed(2)}`;
}

function statusBadge(subscription) {
  if (!subscription) return <Badge colorScheme="gray">No subscription</Badge>;
  if (subscription.status === 'canceled') return <Badge colorScheme="red">Cancelled</Badge>;
  if (subscription.cancelAtPeriodEnd) {
    return <Badge colorScheme="orange">Cancelling at period end</Badge>;
  }
  if (subscription.status === 'active') return <Badge colorScheme="green">Active</Badge>;
  return <Badge colorScheme="yellow">{subscription.status}</Badge>;
}

// Confirmation dialog for subscription actions. Matches the application's
// existing dialog pattern (see components/NewChatDialog.js): Chakra
// AlertDialog, isCentered, primary #CF008A / secondary #037A9B buttons,
// 12px border radius.
function SubscriptionConfirmDialog({
  isOpen,
  onClose,
  onConfirm,
  title,
  body,
  confirmLabel,
  isLoading,
}) {
  const cancelRef = useRef();

  return (
    <AlertDialog
      isOpen={isOpen}
      leastDestructiveRef={cancelRef}
      onClose={onClose}
      isCentered
    >
      <AlertDialogOverlay>
        <AlertDialogContent borderRadius="16px">
          <AlertDialogHeader fontSize="lg" fontWeight="600" color="#242424">
            {title}
          </AlertDialogHeader>

          <AlertDialogBody fontSize="14px" color="#6D6D6D">
            {body}
          </AlertDialogBody>

          <AlertDialogFooter>
            <Button
              ref={cancelRef}
              onClick={onClose}
              bg="#FFFFFF"
              color="#037A9B"
              border="1px solid #00C2E5"
              borderRadius="12px"
              fontWeight="600"
              _hover={{ bg: '#ffffff', color: '#CF008A', borderColor: '#FF9CEE' }}
              _active={{ bg: '#FFFFFF' }}
            >
              Close
            </Button>

            <Button
              onClick={onConfirm}
              ml={3}
              bg="#CF008A"
              color="#FFFFFF"
              borderRadius="12px"
              fontWeight="600"
              isLoading={isLoading}
              _hover={{ bg: '#D933A1' }}
              _active={{ bg: '#CF008A' }}
            >
              {confirmLabel}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
}

// Early Access offer card — shown to NEW / unsubscribed users during the
// campaign. Replaces the normal Starter offer as a checkout option. The
// normal `starter` BillingPlan / $100 Stripe Price stays in the backend for
// existing subscriptions + historical billing but is NOT offered here.
//
// Visual design mirrors the "Early Access pricing" reference card:
//   * light-pink badge pill (dark-pink circle w/ gauge icon + uppercase label),
//   * crossed-out original price next to the large bold discounted price,
//   * three icon detail lines (calendar / trending-up / rocket),
//   * right-aligned magenta "Subscribe now" button.
function EarlyAccessOfferCard({ offer, busy, onSubscribe }) {
  if (!offer) return null;
  return (
    <HStack justify="space-between" align='end' wrap="wrap" gap="24px">
      <VStack align="start" spacing="14px" flex="1" minW="260px">
        {/* Badge: light pink pill w/ dark pink gauge circle + uppercase label */}
        <Flex
          align="center"
          gap="10px"
          bg="#FDF0F8"
          borderRadius="10px"
          px="14px"
          py="6px"
        >
          <Flex
            align="center"
            justify="center"
            // bg="#CF008A"
            // borderRadius="10px"
            // boxSize="26px"
            color="gold"
          >
            <Sparkle size={16} strokeWidth={2.5} />
          </Flex>
          <Text fontSize="12px" fontWeight="700" letterSpacing="0.4px" color="#CF008A">
            {offer.badge}
          </Text>
        </Flex>

        {/* Pricing row: crossed-out original + large bold discounted */}
        <HStack align="baseline" spacing="10px" wrap="wrap">
          <Text
            fontSize="20px"
            fontWeight="700"
            color="#9A9A9A"
            textDecoration="line-through"
          >
            {offer.originalPrice}
          </Text>
          <Text fontSize="20px" fontWeight="800" lineHeight="1.15" color="#CF008A">
            {offer.priceLabel}
          </Text>
        </HStack>

        {/* Details */}
        <VStack align="start" spacing="8px">
          <HStack spacing="10px">
            <Calendar size={15} color="#CF008A" />
            <Text fontSize="14px" color="#6D6D6D">{offer.rateNote}</Text>
          </HStack>
          <HStack spacing="10px">
            <TrendingUp size={15} color="#CF008A" />
            <Text fontSize="14px" color="#6D6D6D">{offer.increaseNote}</Text>
          </HStack>
          <HStack spacing="10px">
            <Rocket size={15} color="#CF008A" />
            <Text fontSize="14px" color="#6D6D6D">{offer.cta}</Text>
          </HStack>
        </VStack>
      </VStack>

      <Button
        colorScheme="pink"
        bg="#CF008A"
        _hover={{ bg: '#D933A1' }}
        _active={{ bg: '#CF008A' }}
        size="md"
        px="28px"
        borderRadius="12px"
        isLoading={busy}
        onClick={() => onSubscribe(offer.planKey)}
      >
        Subscribe
      </Button>
    </HStack>
  );
}

export default function Billing() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [isSidebarOpen, setSidebarOpen] = useState(false);
  // 'cancel' | 'reactivate' | null — which confirmation dialog is open.
  const [pendingAction, setPendingAction] = useState(null);
  const [searchParams] = useSearchParams();

  const load = useCallback(async () => {
    try {
      const { data: result } = await apiCall(`${API}/api/billing`, { silent: true });
      setData(result);
    } catch (err) {
      notify.error('Failed to load billing', err.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Checkout return notifications (?billing=success | ?billing=cancelled).
  // The decision logic lives in utils/billingHelpers (pure + unit-testable):
  //   * success is NEVER trusted from the query param alone — it is shown only
  //     after the billing-state load completes AND the backend confirms an
  //     active subscription,
  //   * the ref guard + stable notification id make it StrictMode/dedup safe
  //     (exactly one toast),
  //   * the handled param is removed from the URL via history replacement so
  //     refresh/revisit cannot re-trigger it.
  useBillingOutcomeNotification({ loading, data, searchParams, notify });

  const handleSubscribe = async (planKey) => {
    setBusy(true);
    try {
      const { data: result } = await apiCall(`${API}/api/billing/checkout`, {
        method: 'POST',
        body: { planKey },
      });
      if (result?.url) {
        // Redirect to Stripe-hosted Checkout (subscription mode).
        window.location.href = result.url;
        return;
      }
      notify.error('Checkout failed', 'No checkout URL was returned.');
    } catch (err) {
      notify.error('Checkout failed', err.data?.error || err.message);
    } finally {
      setBusy(false);
    }
  };

  // Cancel is NOT immediate: it requires explicit confirmation and takes
  // effect at the END of the current billing period. The dialog explains
  // this; the API is only called after the user confirms.
  const handleCancel = () => {
    if (!data?.subscription) return;
    setPendingAction('cancel');
  };

  const confirmCancel = async () => {
    if (!data?.subscription) return;
    setBusy(true);
    try {
      await apiCall(`${API}/api/billing/subscription/cancel`, {
        method: 'POST',
        body: { stripeSubscriptionId: data.subscription.stripeSubscriptionId },
      });
      notify.info('Cancellation scheduled', 'Your subscription will end at the end of the current billing period.');
      setPendingAction(null);
      await load();
    } catch (err) {
      notify.error('Failed to cancel', err.data?.error || err.message);
    } finally {
      setBusy(false);
    }
  };

  // Reactivation (only when cancellation is scheduled at period end) also
  // requires explicit confirmation before the API is called.
  const handleReactivate = () => {
    if (!data?.subscription) return;
    setPendingAction('reactivate');
  };

  const confirmReactivate = async () => {
    if (!data?.subscription) return;
    setBusy(true);
    try {
      await apiCall(`${API}/api/billing/subscription/reactivate`, {
        method: 'POST',
        body: { stripeSubscriptionId: data.subscription.stripeSubscriptionId },
      });
      notify.success('Subscription reactivated', 'Your subscription will renew as normal.');
      setPendingAction(null);
      await load();
    } catch (err) {
      notify.error('Failed to reactivate', err.data?.error || err.message);
    } finally {
      setBusy(false);
    }
  };

  const subscription = data?.subscription;
  const periods = data?.billingPeriods || [];
  // The Early Access offer replaces the normal Starter offer for NEW /
  // unsubscribed users. Subscribed users keep their existing plan (no
  // migration logic). The offer is shown until it is manually removed after
  // the global 6-month campaign ends.
  const offer = subscription ? EARLY_ACCESS_OFFER : EARLY_ACCESS_OFFER;

  return (
    <Box minH="100vh">
      <Navbar
        showBrandLogo
        onToggleSidebar={() => setSidebarOpen(prev => !prev)}
      />

      {/* Mobile close button when sidebar is open */}
      {isSidebarOpen && (
        <IconButton
          icon={<CloseIcon boxSize={3} color="#CF008A" />}
          aria-label="Close Sidebar"
          display={{ base: "inline-flex", md: "none" }}
          position="fixed"
          top="10px"
          right="16px"
          zIndex={1350}
          bg="white"
          border="2px solid #CF008A"
          color="#242424"
          borderRadius="12px"
          _hover={{ bg: "#fff", color: "#CF008A", border: "2px solid #CF008A" }}
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <Box display={{ base: "block", md: "none" }}>
        <Sidebar
          isOpen={isSidebarOpen}
          onClose={() => setSidebarOpen(false)}
          onNewProject={() => navigate('/')}
          onSelectProject={(id) => navigate(`/chat/${id}`)}
        />
      </Box>

      <Flex
        maxW="880px"
        mx="auto"
        mt="clamp(90px, 12vh, 130px)"
        px="clamp(16px, 2vw, 32px)"
        pb="60px"
        direction="column"
      >
        {/* Back/close header — same pattern as the Profile page: navigate(-1)
            returns the user to the page they were on before opening Billing. */}
        <Flex align="center" gap="clamp(12px, 1.2vw, 16px)" mb="clamp(20px, 2vh, 28px)">
          <IconButton
            onClick={() => navigate('/')} //navigate(-1)
            aria-label="Back"
            icon={<ArrowBackIcon boxSize="clamp(18px, 1.4vw, 22px)" />}
            variant="ghost"
            size="md"
            borderRadius="full"
            color="#242424"
            _hover={{ bg: "#F5F5F5", color: "#CF008A" }}
          />
          <Heading as="h1" fontSize="clamp(22px, 2.4vw, 28px)" color="#242424">
            Subscription &amp; Billing
          </Heading>
        </Flex>

        <VStack spacing="24px" align="stretch">
          {loading ? (
            <Flex justify="center" py="60px"><Spinner color="#CF008A" /></Flex>
          ) : (
            <>
              {/* Plan / subscription card */}
              <Card borderRadius="16px" border="1px solid #ECECEC" boxShadow="none" bg="white">
                <CardBody p="24px">
                  {!subscription ? (
                    <EarlyAccessOfferCard
                      offer={offer}
                      busy={busy}
                      onSubscribe={handleSubscribe}
                    />
                  ) : (
                    <HStack justify="space-between" align="flex-start" wrap="wrap" gap="16px">
                      {/* <VStack align="start" spacing="6px">
                      <Text fontSize="18px" fontWeight="semibold" color="#242424">
                        Starter — $100 / month
                      </Text>
                      <Text fontSize="14px" color="#6D6D6D">
                        Each successful payment includes AI credits for use on Alpherra.
                      </Text>
                      <HStack spacing="12px" wrap="wrap" pt="4px">
                        {statusBadge(subscription)}
                        {subscription.cancelAt && (
                          <Text fontSize="13px" color="#6D6D6D">
                            Active until {formatDate(subscription.cancelAt)}
                          </Text>
                        )}
                      </HStack>
                    </VStack> */}
                      <HStack justify="space-between" align='end' wrap="wrap" gap="24px">
                        <VStack align="start" spacing="14px" flex="1" minW="260px">
                          {/* Badge: light pink pill w/ dark pink gauge circle + uppercase label */}
                          <Flex
                            align="center"
                            gap="10px"
                            bg="#FDF0F8"
                            borderRadius="10px"
                            px="14px"
                            py="6px"
                          >
                            <Flex
                              align="center"
                              justify="center"
                              // bg="#CF008A"
                              // borderRadius="10px"
                              // boxSize="26px"
                              color="gold"
                            >
                              <Sparkle size={16} strokeWidth={2.5} />
                            </Flex>
                            <Text fontSize="12px" fontWeight="700" letterSpacing="0.4px" color="#CF008A">
                              {offer.badge}
                            </Text>
                          </Flex>

                          {/* Pricing row: crossed-out original + large bold discounted */}
                          <HStack align="baseline" spacing="10px" wrap="wrap">
                            <Text
                              fontSize="20px"
                              fontWeight="700"
                              color="#9A9A9A"
                              textDecoration="line-through"
                            >
                              {offer.originalPrice}
                            </Text>
                            <Text fontSize="20px" fontWeight="800" lineHeight="1.15" color="#CF008A">
                              {offer.priceLabel}
                            </Text>
                          </HStack>

                          {/* Details */}
                          <VStack align="start" spacing="8px">
                            <HStack spacing="10px">
                              <Calendar size={15} color="#CF008A" />
                              <Text fontSize="14px" color="#6D6D6D">{offer.rateNote}</Text>
                            </HStack>
                            <HStack spacing="10px">
                              <TrendingUp size={15} color="#CF008A" />
                              <Text fontSize="14px" color="#6D6D6D">{offer.increaseNote}</Text>
                            </HStack>
                            {/* <HStack spacing="10px">
                              <Rocket size={15} color="#CF008A" />
                              <Text fontSize="14px" color="#6D6D6D">{}</Text>
                            </HStack> */}
                            <HStack spacing="12px" wrap="wrap" pt="4px">
                              {statusBadge(subscription)}
                              {subscription.cancelAt && (
                                <Text fontSize="13px" color="#6D6D6D">
                                  Active until {formatDate(subscription.cancelAt)}
                                </Text>
                              )}
                            </HStack>
                          </VStack>
                        </VStack>
                      </HStack>

                      {subscription.cancelAtPeriodEnd ? (
                        <Button
                          variant="outline"
                          borderColor="#CF008A"
                          color="#CF008A"
                          _hover={{ bg: '#FDF0F8' }}
                          size="md"
                          isLoading={busy}
                          onClick={handleReactivate}
                        >
                          Reactivate subscription
                        </Button>
                      ) : (
                        <Button
                          variant="outline"
                          borderColor="#CF008A"
                          color="#CF008A"
                          _hover={{ bg: '#FDF0F8' }}
                          size="md"
                          isLoading={busy}
                          onClick={handleCancel}
                        >
                          Cancel subscription
                        </Button>
                      )}
                    </HStack>
                  )}
                </CardBody>
              </Card>

              {/* Billing periods */}
              <Card borderRadius="16px" border="1px solid #ECECEC" boxShadow="none" bg="white">
                <CardBody p="24px">
                  <Text fontSize="16px" fontWeight="semibold" color="#242424" mb="12px">
                    Billing history
                  </Text>
                  {periods.length === 0 ? (
                    <Text fontSize="14px" color="#6D6D6D">No billing periods yet.</Text>
                  ) : (
                    <VStack align="stretch" divider={<Divider borderColor="#ECECEC" />} spacing="0">
                      {periods.map((p) => (
                        <Flex
                          key={p.id}
                          justify="space-between"
                          align="center"
                          py="12px"
                          wrap="wrap"
                          gap="8px"
                        >
                          <VStack align="start" spacing="2px">
                            <Text fontSize="14px" fontWeight="medium" color="#242424">
                              {displayPeriodRange(p, subscription)}
                            </Text>
                            <Text fontSize="13px" color="#6D6D6D">
                              Paid {formatMoney(p.amountPaid)} {currencyLabel(p.currency)}
                            </Text>
                          </VStack>
                          {/* <VStack align="end" spacing="2px">
                          <Text fontSize="14px" fontWeight="semibold" color="#037A9B">
                            AI credits added
                          </Text>
                        </VStack> */}
                        </Flex>
                      ))}
                    </VStack>
                  )}
                </CardBody>
              </Card>
            </>
          )}
        </VStack>
      </Flex>

      {/* Cancel-subscription confirmation — cancellation takes effect at the
          END of the current billing period, never immediately. */}
      <SubscriptionConfirmDialog
        isOpen={pendingAction === 'cancel'}
        onClose={() => setPendingAction(null)}
        onConfirm={confirmCancel}
        isLoading={busy}
        title="Cancel subscription?"
        body={
          <>
            Cancelling ends your subscription at the end of the current billing
            period. Your subscription stays active until then, and this is not
            an immediate cancellation or refund. You can reactivate before the
            period ends.
          </>
        }
        confirmLabel="Confirm cancellation"
      />

      {/* Reactivation confirmation — restores normal recurring renewal. */}
      <SubscriptionConfirmDialog
        isOpen={pendingAction === 'reactivate'}
        onClose={() => setPendingAction(null)}
        onConfirm={confirmReactivate}
        isLoading={busy}
        title="Reactivate subscription?"
        body={
          <>
            Reactivating restores your normal recurring renewal. Your
            subscription will continue past the end of the current billing
            period instead of being cancelled.
          </>
        }
        confirmLabel="Confirm reactivation"
      />
    </Box>
  );
}
